/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect } from 'react';
import useRequests from '../../hooks/useRequests';
import { useTranslation } from 'react-i18next';
import { useQueryParams } from '../../hooks/useQueryParams';
import { PROD_API_URL } from '../../utils/env';
import { CircularProgress } from '@mui/material';

const English = ({ isCompleted, askingEmail }) => {
  if (askingEmail) {
    return '';
  }

  if (isCompleted) {
    return (
      <section style={{ padding: '2rem', maxWidth: '900px', margin: 'auto', fontFamily: 'sans-serif', textAlign: 'center' }}>
        <h2 style={{ fontSize: '2rem', marginBottom: '1rem', color: '#28a745' }}>✅ Connection Successful</h2>
        <p style={{ fontSize: '1.1rem', color: '#444', marginBottom: '1.5rem' }}>
          Your Instagram Business account is now connected through Facebook.
        </p>

        <div
          style={{
            backgroundColor: '#e9f7ef',
            padding: '1rem',
            borderRadius: '0.5rem',
            fontSize: '1rem',
            color: '#155724',
            border: '1px solid #c3e6cb',
            maxWidth: '700px',
            margin: 'auto',
          }}
        >
          🎉 You can now receive direct messages in real time and set up automatic replies for your customers. All conversations will flow through
          your connected workflows and can be managed from our platform.
        </div>
      </section>
    );
  }

  return (
    <section style={{ padding: '2rem', maxWidth: '900px', margin: 'auto', fontFamily: 'sans-serif' }}>
      <h2 style={{ fontSize: '2rem', marginBottom: '1.5rem' }}>Connect Your Instagram Account to Automate Direct Messages</h2>
      <p style={{ fontSize: '1.1rem', color: '#444' }}>
        Using a secure connection via Facebook, you can link your Instagram Business account and start receiving and responding to direct messages
        (DMs) automatically. This process is officially supported by Meta.
      </p>

      <h3 style={{ marginTop: '2rem', marginBottom: '1.5rem' }}>✅ What do you need to get started?</h3>
      <ul style={{ listStyleType: 'disc', paddingLeft: '1.5rem', color: '#333', textAlign: 'left' }}>
        <li>An Instagram account set as Business or Creator</li>
        <li>Your Instagram account must be connected to a Facebook Page</li>
        <li>Access to a Facebook account that manages that Page</li>
        <li>Approve the requested permissions during the connection process</li>
      </ul>

      <div
        style={{
          marginTop: '1.5rem',
          backgroundColor: '#f8f9fa',
          padding: '1rem',
          borderRadius: '0.5rem',
          fontSize: '0.95rem',
          color: '#333',
        }}
      >
        💡 <strong>Note:</strong> Once connected, we’ll enable real-time message reception and we’ll be able to create automatic responses to
        streamline customer service on Instagram.
      </div>
    </section>
  );
};

const Spanish = ({ isCompleted, askingEmail }) => {
  if (askingEmail) {
    return '';
  }

  if (isCompleted) {
    return (
      <section style={{ padding: '2rem', maxWidth: '900px', margin: 'auto', fontFamily: 'sans-serif', textAlign: 'center' }}>
        <h2 style={{ fontSize: '2rem', marginBottom: '1rem', color: '#28a745' }}>✅ Conexión completada</h2>
        <p style={{ fontSize: '1.1rem', color: '#444', marginBottom: '1.5rem' }}>
          Tu cuenta de Instagram Business ha sido conectada exitosamente a través de Facebook.
        </p>

        <div
          style={{
            backgroundColor: '#e9f7ef',
            padding: '1rem',
            borderRadius: '0.5rem',
            fontSize: '1rem',
            color: '#155724',
            border: '1px solid #c3e6cb',
            maxWidth: '700px',
            margin: 'auto',
          }}
        >
          🎉 Ahora puedes recibir mensajes directos en tiempo real y configurar respuestas automáticas para tus clientes. Todas las conversaciones se
          gestionarán desde nuestra plataforma y se integrarán con tus flujos personalizados.
        </div>
      </section>
    );
  }
  return (
    <section style={{ padding: '2rem', maxWidth: '900px', margin: 'auto', fontFamily: 'sans-serif' }}>
      <h2 style={{ fontSize: '2rem', marginBottom: '1.5rem' }}>Conecta tu cuenta de Instagram para automatizar tus mensajes directos</h2>
      <p style={{ fontSize: '1.1rem', color: '#444' }}>
        A través de una conexión segura con Facebook, puedes vincular tu cuenta de Instagram Business y comenzar a recibir y responder mensajes
        directos (DMs) de forma automática. Este proceso está respaldado por Meta.
      </p>

      <h3 style={{ marginTop: '2rem', marginBottom: '1.5rem' }}>✅ ¿Qué necesitas para comenzar?</h3>
      <ul style={{ listStyleType: 'disc', paddingLeft: '1.5rem', color: '#333', textAlign: 'left' }}>
        <li>Una cuenta de Instagram configurada como Business o Creador</li>
        <li>Tu cuenta de Instagram debe estar conectada a una Página de Facebook</li>
        <li>Acceso a una cuenta de Facebook que administre esa página</li>
        <li>Autorizar los permisos solicitados durante el proceso de conexión</li>
      </ul>

      <div
        style={{
          marginTop: '1.5rem',
          backgroundColor: '#f8f9fa',
          padding: '1rem',
          borderRadius: '0.5rem',
          fontSize: '0.95rem',
          color: '#333',
        }}
      >
        💡 <strong>Importante:</strong> Al conectar tu cuenta, activaremos la recepción de mensajes en tiempo real y podremos configurar respuestas
        automáticas para mejorar la atención de tus clientes en Instagram.
      </div>
    </section>
  );
};

const FacebookOnboarding = () => {
  const requests = useRequests();
  const { i18n } = useTranslation();
  const { code: authCode } = useQueryParams();
  const [email, setEmail] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [completed, setCompleted] = React.useState(false);
  const [error, setError] = React.useState(false);

  const isEnglish = i18n.language === 'en';
  async function completeProcess(code) {
    setLoading(true);
    const resp = await requests.post(`${PROD_API_URL}/meta/facebook-onboarding`, {
      code,
      email,
    });

    if (resp.status === 200) {
      setCompleted(true);
    } else {
      setError(true);
      console.error('Error:', resp.data);
    }

    setLoading(false);
  }

  const launchFacebookSignup = () => {
    const clientId = '2219470115115225';
    const redirectUri = encodeURIComponent('https://kesildigital.com/facebook-onboarding');
    const scope = ['pages_show_list', 'instagram_basic', 'pages_manage_metadata', 'instagram_manage_messages', 'pages_messaging'].join(',');

    const authUrl = `https://www.facebook.com/v22.0/dialog/oauth?client_id=${clientId}&redirect_uri=${redirectUri}&scope=${scope}&response_type=code`;

    window.location.href = authUrl;
  };

  return (
    <section className="wpo-contact-pg-section">
      <div className="container">
        <div className="row">
          <div className="wpo-contact-title">
            {isEnglish ? (
              <English isCompleted={completed} askingEmail={!completed && authCode} />
            ) : (
              <Spanish isCompleted={completed} askingEmail={!completed && authCode} />
            )}

            {!authCode && (
              <button
                onClick={launchFacebookSignup}
                style={{
                  marginTop: '2rem',
                  backgroundColor: '#007bff',
                  color: '#fff',
                  border: 'none',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '0.5rem',
                  fontSize: '16px',
                  cursor: 'pointer',
                }}
              >
                {isEnglish ? 'Connect' : 'Conectar'}
              </button>
            )}

            {authCode && !completed && (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  completeProcess(authCode);
                }}
                style={{ marginTop: '10rem', textAlign: 'center', maxWidth: '460px', margin: 'auto' }}
              >
                <label htmlFor="email" style={{ display: 'block', marginBottom: '0.5rem' }}>
                  {isEnglish ? 'Enter your email to complete registration:' : 'Ingresa tu correo electrónico para completar el registro:'}
                </label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  style={{
                    padding: '0.5rem',
                    borderRadius: '0.25rem',
                    border: '1px solid #ccc',
                    width: '100%',
                    maxWidth: '460px',
                    marginBottom: '1rem',
                  }}
                />
                {error && (
                  <div style={{ color: 'red', marginBottom: '1rem' }}>
                    {isEnglish ? 'An error occurred. Please try again.' : 'Ocurrió un error. Por favor, inténtalo de nuevo.'}
                  </div>
                )}
                <br />
                <button
                  type="submit"
                  style={{
                    backgroundColor: '#007bff',
                    color: '#fff',
                    border: 'none',
                    padding: '0.5rem 1rem',
                    borderRadius: '0.25rem',
                    cursor: 'pointer',
                    width: '100%',
                  }}
                >
                  {loading ? <CircularProgress size={22} style={{ color: '#fff' }} /> : isEnglish ? 'Complete Registration' : 'Completar Registro'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
      <section className="wpo-contact-map-section">
        <div className="wpo-contact-map"></div>
      </section>
    </section>
  );
};

export default FacebookOnboarding;
